import { ENGAGEMENT_BAND } from 'enums/class-management';
import {
  CLASS_IN_FOCUS_DISPLAY,
  CLASS_LIVE_STATUS_BUCKET
} from 'enums/class-session';
import { UNLOCK_REQUEST_STATUS } from 'enums/team';
import type { AiReviewedUnlockStatus } from 'interfaces/class-session.interface';
import {
  CHIP_VARIANT,
  SESSION_TASK_ROW_CONTENT,
  SESSION_TASKS_EDITOR_MODE
} from 'enums/ui';

/** Bounds for the teacher-entered session duration (minutes) in the start modal. */
export const FOCUS_SESSION = {
  MIN_LENGTH_MINUTES: 5,
  MAX_LENGTH_MINUTES: 480,
  DEFAULT_LENGTH_MINUTES: 50
};

/** One-tap duration choices in the start-session modal (minutes). */
export const FOCUS_SESSION_DURATION_PRESETS_MINUTES = [
  15, 25, 30, 45, 50, 60
] as const;

/** Re-render cadence for the live session countdown. */
export const SESSION_COUNTDOWN_TICK_MS = 1_000;

/** Collapsed allow-list chip preview count in the start-session modal. */
export const START_SESSION_ALLOWLIST_PREVIEW_CHIP_COUNT = 4;

/** Top-N rows shown for the concern teaser, and per band for the engagement-band teaser. */
export const CLASS_REPORTS_TEASER_LIMIT = 3;

/**
 * Engagement band cut-offs on a student's whole-percent average focus: High is
 * strictly above HIGH_ABOVE, Low strictly below LOW_BELOW, Medium the inclusive
 * range between (so exactly 80% and exactly 50% are both Medium).
 */
export const ENGAGEMENT_BAND_THRESHOLD_PCT = {
  HIGH_ABOVE: 80,
  LOW_BELOW: 50
} as const;

/** Display order of the engagement bands; students without data come last. */
export const ENGAGEMENT_BAND_ORDER: readonly ENGAGEMENT_BAND[] = [
  ENGAGEMENT_BAND.HIGH,
  ENGAGEMENT_BAND.MEDIUM,
  ENGAGEMENT_BAND.LOW,
  ENGAGEMENT_BAND.NO_DATA
];

export const ENGAGEMENT_BAND_LABEL_KEYS: Record<ENGAGEMENT_BAND, string> = {
  [ENGAGEMENT_BAND.HIGH]: 'class_mode.engagement_band_high',
  [ENGAGEMENT_BAND.MEDIUM]: 'class_mode.engagement_band_medium',
  [ENGAGEMENT_BAND.LOW]: 'class_mode.engagement_band_low',
  [ENGAGEMENT_BAND.NO_DATA]: 'class_mode.engagement_band_no_data'
};

/** Range copy interpolated with ENGAGEMENT_BAND_THRESHOLD_PCT; no range for students without data. */
export const ENGAGEMENT_BAND_RANGE_KEYS: Record<
  ENGAGEMENT_BAND,
  string | null
> = {
  [ENGAGEMENT_BAND.HIGH]: 'class_mode.engagement_band_high_range',
  [ENGAGEMENT_BAND.MEDIUM]: 'class_mode.engagement_band_medium_range',
  [ENGAGEMENT_BAND.LOW]: 'class_mode.engagement_band_low_range',
  [ENGAGEMENT_BAND.NO_DATA]: null
};

export const ENGAGEMENT_BAND_CHIP_VARIANT: Record<
  ENGAGEMENT_BAND,
  CHIP_VARIANT
> = {
  [ENGAGEMENT_BAND.HIGH]: CHIP_VARIANT.SUCCESS,
  [ENGAGEMENT_BAND.MEDIUM]: CHIP_VARIANT.WARNING,
  [ENGAGEMENT_BAND.LOW]: CHIP_VARIANT.DANGER,
  [ENGAGEMENT_BAND.NO_DATA]: CHIP_VARIANT.FILLED
};

/** Minimum optional duration (minutes) for a session task in the start modal. */
export const MIN_SESSION_TASK_DURATION_MINUTES = 1;

/** Opacity while reordering session tasks in the start-session editor. */
export const SESSION_TASK_DND_OPACITY = {
  NOT_DRAGGING: 1,
  DRAGGING: 0.5
} as const;

/** Tooltip i18n keys for draft vs live session-tasks editors. */
export const SESSION_TASKS_EDITOR_HINT_KEYS: Record<
  SESSION_TASKS_EDITOR_MODE,
  string
> = {
  [SESSION_TASKS_EDITOR_MODE.DRAFT]: 'class_mode.tasks_can_amend_live_hint',
  [SESSION_TASKS_EDITOR_MODE.LIVE]: 'class_mode.session_tasks_hot_sync_hint'
};

export const SESSION_TASKS_EDITOR_CAN_ADD: Record<
  SESSION_TASKS_EDITOR_MODE,
  boolean
> = {
  [SESSION_TASKS_EDITOR_MODE.DRAFT]: true,
  [SESSION_TASKS_EDITOR_MODE.LIVE]: false
};

export const SESSION_TASKS_EDITOR_CAN_REMOVE: Record<
  SESSION_TASKS_EDITOR_MODE,
  boolean
> = {
  [SESSION_TASKS_EDITOR_MODE.DRAFT]: true,
  [SESSION_TASKS_EDITOR_MODE.LIVE]: false
};

export const SESSION_TASKS_EDITOR_ROW_CONTENT: Record<
  SESSION_TASKS_EDITOR_MODE,
  SESSION_TASK_ROW_CONTENT
> = {
  [SESSION_TASKS_EDITOR_MODE.DRAFT]: SESSION_TASK_ROW_CONTENT.DISPLAY,
  [SESSION_TASKS_EDITOR_MODE.LIVE]: SESSION_TASK_ROW_CONTENT.EDITABLE
};

/** Teacher live-board presence channel (`presence-class-<teamId>`). Students are not authorized here. */
export const CLASS_PRESENCE_CHANNEL_PREFIX = 'presence-class-';

/**
 * Minimum typed characters before the restricted candidate-members search
 * queries the backend (`?q=` — teachers can't browse the school roster).
 */
export const CANDIDATE_SEARCH_MIN_QUERY_LENGTH = 2;

/** Em dash for empty class table cells (e.g. member name when only email exists). */
export const EMPTY_COLUMN_PLACEHOLDER = '—';

/**
 * Fallback only — presence transitions (join/leave, in-focus, offline) arrive
 * via Pusher `class-participant-updated`; this poll reconciles missed events
 * and the server sweep's going-offline detection latency (~60s).
 */
export const LIVE_ROSTER_POLL_INTERVAL_MS = 30_000;

/**
 * The backend flags `app_offline` after only 15s of heartbeat silence, which
 * trips on brief Wi-Fi blips. Until the silence passes this threshold the
 * roster shows a softer "Connection issue" instead of "App quit or offline".
 */
export const APP_OFFLINE_AFTER_SILENCE_SECONDS = 120;

/** Re-render cadence for the "last heartbeat … ago" caption under roster chips. */
export const HEARTBEAT_AGE_TICK_MS = 1_000;

/**
 * Fallback poll for unlock requests while a class session is live (Pusher
 * `class-unlock-requested` is primary for new pendings). Slower than roster
 * (30s): creates are push-driven; this interval only reconciles missed / stale
 * status. Roster stays faster because students can join any time mid-session.
 */
export const LIVE_UNLOCK_REQUESTS_POLL_INTERVAL_MS = 60_000;

/**
 * Throttle for overview card enrichment refresh when join/leave arrives on a
 * live class that is not the currently open session (no in-memory roster).
 */
export const OVERVIEW_CLASSES_REFRESH_THROTTLE_MS = 2_000;

/** Columns in the resolved unlock-requests table (Item · Status · Requested by · When). */
export const RESOLVED_UNLOCK_COLUMN_COUNT = 4;

/** Statuses the AI sets after a student explains why they need a blocked site/app. */
export const AI_REVIEWED_UNLOCK_STATUSES: readonly AiReviewedUnlockStatus[] = [
  UNLOCK_REQUEST_STATUS.AI_APPROVED,
  UNLOCK_REQUEST_STATUS.AI_REJECTED
];

export const AI_REVIEWED_UNLOCK_STATUS_LABEL_KEYS: Record<
  AiReviewedUnlockStatus,
  string
> = {
  [UNLOCK_REQUEST_STATUS.AI_APPROVED]: 'class_mode.unlock_status_ai_approved',
  [UNLOCK_REQUEST_STATUS.AI_REJECTED]: 'class_mode.unlock_status_ai_rejected'
};

export const FOCUS_PERCENT_BOUNDS = {
  MIN: 0,
  MAX: 100
} as const;

/** Live roster table headers rendered without tooltips (tasks / % use tooltip headers). */
export const CLASS_ROSTER_BASE_HEADER_KEYS = [
  'class_mode.student',
  'class_mode.join_status',
  'class_mode.in_focus_now'
] as const;

/** i18n keys for live "in focus now" chips (excludes dash placeholder). */
export const CLASS_IN_FOCUS_LABEL_KEY = {
  [CLASS_IN_FOCUS_DISPLAY.APP_OFFLINE]: 'class_mode.app_offline',
  [CLASS_IN_FOCUS_DISPLAY.CONNECTION_ISSUE]: 'class_mode.connection_issue',
  [CLASS_IN_FOCUS_DISPLAY.NO_HEARTBEAT]: 'class_mode.no_heartbeat',
  [CLASS_IN_FOCUS_DISPLAY.IN_FOCUS]: 'class_mode.in_focus_now',
  [CLASS_IN_FOCUS_DISPLAY.NOT_IN_FOCUS]: 'class_mode.not_in_focus'
} as const;

export const CLASS_IN_FOCUS_CHIP_VARIANT = {
  [CLASS_IN_FOCUS_DISPLAY.APP_OFFLINE]: CHIP_VARIANT.DANGER,
  [CLASS_IN_FOCUS_DISPLAY.CONNECTION_ISSUE]: CHIP_VARIANT.CAUTION,
  [CLASS_IN_FOCUS_DISPLAY.NO_HEARTBEAT]: CHIP_VARIANT.OUTLINED,
  [CLASS_IN_FOCUS_DISPLAY.IN_FOCUS]: CHIP_VARIANT.SUCCESS,
  [CLASS_IN_FOCUS_DISPLAY.NOT_IN_FOCUS]: CHIP_VARIANT.WARNING
} as const;

export const LOOKBACK_SESSIONS_BOUNDS = {
  MIN: 1,
  MAX: 20
} as const;

/** Live header tiles, left to right. */
export const CLASS_LIVE_STATUS_BUCKET_ORDER: readonly CLASS_LIVE_STATUS_BUCKET[] =
  [
    CLASS_LIVE_STATUS_BUCKET.FOCUSING,
    CLASS_LIVE_STATUS_BUCKET.NOT_FOCUSING,
    CLASS_LIVE_STATUS_BUCKET.OFFLINE,
    CLASS_LIVE_STATUS_BUCKET.WAITING,
    CLASS_LIVE_STATUS_BUCKET.DECLINED_OR_LEFT
  ];

export const CLASS_LIVE_STATUS_BUCKET_LABEL_KEYS: Record<
  CLASS_LIVE_STATUS_BUCKET,
  string
> = {
  [CLASS_LIVE_STATUS_BUCKET.FOCUSING]: 'class_mode.live_bucket_focusing',
  [CLASS_LIVE_STATUS_BUCKET.NOT_FOCUSING]:
    'class_mode.live_bucket_not_focusing',
  [CLASS_LIVE_STATUS_BUCKET.OFFLINE]: 'class_mode.live_bucket_offline',
  [CLASS_LIVE_STATUS_BUCKET.WAITING]: 'class_mode.live_bucket_waiting',
  [CLASS_LIVE_STATUS_BUCKET.DECLINED_OR_LEFT]:
    'class_mode.live_bucket_declined_or_left'
};

export const CLASS_LIVE_STATUS_BUCKET_HINT_KEYS: Record<
  CLASS_LIVE_STATUS_BUCKET,
  string
> = {
  [CLASS_LIVE_STATUS_BUCKET.FOCUSING]: 'class_mode.live_bucket_focusing_hint',
  [CLASS_LIVE_STATUS_BUCKET.NOT_FOCUSING]:
    'class_mode.live_bucket_not_focusing_hint',
  [CLASS_LIVE_STATUS_BUCKET.OFFLINE]: 'class_mode.live_bucket_offline_hint',
  [CLASS_LIVE_STATUS_BUCKET.WAITING]: 'class_mode.live_bucket_waiting_hint',
  [CLASS_LIVE_STATUS_BUCKET.DECLINED_OR_LEFT]:
    'class_mode.live_bucket_declined_or_left_hint'
};

/** Tile number colour per bucket (semantic tokens, dark-mode aware). */
export const CLASS_LIVE_STATUS_BUCKET_VALUE_CLASS: Record<
  CLASS_LIVE_STATUS_BUCKET,
  string
> = {
  [CLASS_LIVE_STATUS_BUCKET.FOCUSING]: 'text-green-700 dark:text-green-400',
  [CLASS_LIVE_STATUS_BUCKET.NOT_FOCUSING]: 'text-amber-600 dark:text-amber-400',
  [CLASS_LIVE_STATUS_BUCKET.OFFLINE]: 'text-red-600 dark:text-red-400',
  [CLASS_LIVE_STATUS_BUCKET.WAITING]:
    'text-gray-700 dark:text-secondary-dark-900',
  [CLASS_LIVE_STATUS_BUCKET.DECLINED_OR_LEFT]:
    'text-gray-700 dark:text-secondary-dark-900'
};

/**
 * Roster order: rows a teacher may need to act on first (offline, off task),
 * then not-yet-joined, then focusing, then declined/left.
 */
export const CLASS_LIVE_STATUS_BUCKET_SORT_RANK: Record<
  CLASS_LIVE_STATUS_BUCKET,
  number
> = {
  [CLASS_LIVE_STATUS_BUCKET.OFFLINE]: 0,
  [CLASS_LIVE_STATUS_BUCKET.NOT_FOCUSING]: 1,
  [CLASS_LIVE_STATUS_BUCKET.WAITING]: 2,
  [CLASS_LIVE_STATUS_BUCKET.FOCUSING]: 3,
  [CLASS_LIVE_STATUS_BUCKET.DECLINED_OR_LEFT]: 4
};

/** End-of-session summary flags joined students below this average focus. */
export const SESSION_SUMMARY_LOW_FOCUS_PCT = 50;
